/**
 * ConsentStore — localStorage-backed consent state + event emitter.
 *
 * Public API exposed as window.jkd_consent.
 */

const STORAGE_KEY = 'jkd_cookie_consent_v1';
const NECESSARY = 'necessary';

export default class ConsentStore {
	constructor({ expirationMonths = 12 } = {}) {
		this.expirationMonths = expirationMonths;
		this.listeners = new Map(); // event → Set<handler>
		this.state = this._read();
	}

	_read() {
		try {
			const raw = localStorage.getItem(STORAGE_KEY);
			if (!raw) return null;
			const parsed = JSON.parse(raw);
			if (!parsed || typeof parsed !== 'object') return null;
			if (parsed.expiresAt && Date.now() > parsed.expiresAt) return null;
			return parsed;
		} catch {
			return null;
		}
	}

	_write(state) {
		const expiresAt = Date.now() + this.expirationMonths * 30 * 24 * 60 * 60 * 1000;
		const next = { ...state, expiresAt, savedAt: Date.now() };
		try {
			localStorage.setItem(STORAGE_KEY, JSON.stringify(next));
		} catch {
			// Privacy-mode browsers may block writes — keep in-memory state regardless.
		}
		this.state = next;
	}

	hasChoice() {
		return this.state !== null;
	}

	has(category) {
		if (category === NECESSARY) return true; // always granted
		if (!this.state) return false;
		return Boolean(this.state.granted?.includes(category));
	}

	grant(categories = []) {
		const granted = Array.from(new Set([NECESSARY, ...categories]));
		this._write({ granted });
		this._emit('granted', { categories: granted });
		this._emit('changed', { categories: granted });
	}

	revoke(categories = []) {
		if (!this.state) return;
		const remaining = (this.state.granted || []).filter(
			(c) => c === NECESSARY || !categories.includes(c)
		);
		this._write({ granted: remaining });
		this._emit('revoked', { categories });
		this._emit('changed', { categories: remaining });
	}

	clear() {
		try {
			localStorage.removeItem(STORAGE_KEY);
		} catch {
			// Privacy-mode browsers may block — still clear in-memory state.
		}
		this.state = null;
		this._emit('cleared', {});
		this._emit('changed', { categories: [NECESSARY] });
	}

	on(event, handler) {
		if (!this.listeners.has(event)) this.listeners.set(event, new Set());
		this.listeners.get(event).add(handler);
		return () => this.listeners.get(event)?.delete(handler);
	}

	_emit(event, payload) {
		this.listeners.get(event)?.forEach((handler) => {
			try {
				handler(payload);
			} catch (err) {
				// Subscriber threw — swallow to keep store stable.
				console.error('[consent]', event, err);
			}
		});
	}
}
